import { toJpeg } from 'html-to-image';
import { useCallback } from 'react';
import { DEFAULT_SCREENSHOT_DARK_BACKGROUND, DEFAULT_SCREENSHOT_LIGHT_BACKGROUND } from '@/constants';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { canvasToImage } from '../utils/dom';

type CaptureElementProps = {
  element: Element;
  background?:
    | typeof DEFAULT_SCREENSHOT_DARK_BACKGROUND
    | typeof DEFAULT_SCREENSHOT_LIGHT_BACKGROUND;
};

export const useCaptureScreenshot = () => {
  const callbacks = useAtomValue(state.globalCallbacks);
  const captureElement = useCallback(
    ({ element, background = DEFAULT_SCREENSHOT_DARK_BACKGROUND }: CaptureElementProps) => {
      return new Promise<string | undefined>((resolve) => {
        // Filter function to handle elements before conversion
        const filter = (node: HTMLElement) => {
          // Skip invisible elements
          if (node.style && node.style.display === 'none') {
            return false;
          }
          return true;
        };

        // Setup options for html-to-image
        const options = {
          quality: 0.8,
          backgroundColor: background,
          pixelRatio: window.devicePixelRatio || 1,
          skipAutoScale: false,
          cacheBust: true,
          filter,
          // Function to prepare the DOM before capturing
          beforeDraw: (documentClone: Document) => {
            // Add fallback font styles
            const fallbackFontStyle = `
              /* Fallback font definitions */
              * {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol' !important;
              }

              @font-face {
                font-family: 'Inter';
                font-style: normal;
                font-display: swap;
                src: local('Inter'), local('Inter-Regular'), local('Arial');
              }

              @font-face {
                font-family: 'Roboto';
                font-style: normal;
                font-display: swap;
                src: local('Roboto'), local('Roboto-Regular'), local('Arial');
              }
            `;

            const head = documentClone.head || documentClone.getElementsByTagName('head')[0];
            const fallbackStyle = documentClone.createElement('style');
            fallbackStyle.textContent = fallbackFontStyle;
            head.appendChild(fallbackStyle);

            try {
              // Copy all styles from the original document
              const styles = document.querySelectorAll('style, link[rel="stylesheet"]');
              styles.forEach((style) => {
                const clone = style.cloneNode(true);
                head.appendChild(clone);
              });
            } catch (e) {
              console.warn('Error copying styles:', e);
            }

            // Fix SVG dimensions
            const svgElements = documentClone.querySelectorAll('svg');
            svgElements.forEach((svg) => {
              svg.setAttribute('width', svg.getBoundingClientRect().width.toString());
              svg.setAttribute('height', svg.getBoundingClientRect().height.toString());
            });

            // Apply computed styles to ensure proper rendering
            const allElements = documentClone.querySelectorAll('*');
            allElements.forEach((el) => {
              if (el instanceof HTMLElement) {
                const computedStyle = window.getComputedStyle(el);
                const criticalProps = [
                  'font-family',
                  'font-size',
                  'font-weight',
                  'font-style',
                  'color',
                  'background-color',
                  'text-align',
                  'line-height',
                  'padding',
                  'margin',
                  'border',
                  'display',
                  'position',
                  'white-space',
                ];

                criticalProps.forEach((prop) => {
                  const value = computedStyle.getPropertyValue(prop);
                  if (value) {
                    el.style.setProperty(prop, value, 'important');
                  }
                });
              }
            });

            return Promise.resolve();
          },
        };

        // Convert the element to a JPEG image
        toJpeg(element as HTMLElement, options)
          .then((dataUrl: string) => {
            resolve(dataUrl);
          })
          .catch((error: Error) => {
            console.error('Error capturing screenshot:', error);
            // Fallback to canvas if available
            const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();
            const fallbackImage = canvasToImage(htmlCanvas, {
              fillStyle: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
            });
            resolve(fallbackImage);
          });
      });
    },
    [callbacks]
  );

  return { captureElement };
};
